import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  signal,
  computed,
  input
} from '@angular/core';

import {
  CommonModule
} from '@angular/common';

import {
  FormControl,
  ReactiveFormsModule
} from '@angular/forms';

import {
  MatAutocomplete,
  MatAutocompleteModule
} from '@angular/material/autocomplete';

import {
  MatDialog
} from '@angular/material/dialog';

import {
  MatFormFieldModule
} from '@angular/material/form-field';

import {
  MatInputModule
} from '@angular/material/input';

import {
  MatIconModule
} from '@angular/material/icon';

import {
  MatDatepickerModule
} from '@angular/material/datepicker';

import {
  MatNativeDateModule
} from '@angular/material/core';

import {
  MatTooltipModule
} from '@angular/material/tooltip';

// import {
//   RegexLib
// } from '../../../../core/constants/regex.constants';

// import {
//   SharedService
// } from '../../../../core/services/shared.service';

import {
  ConfirmationDialog
} from '../confirmation-dialog/confirmation-dialog';
import { ConfirmationDialogButton } from '../../../core/enums/confirmationDialogButton.enum';
import { SharedService } from '../../../core/services/shared.service';
import { RegexLib } from '../../../core/constants/regex.constants';

// import {
//   ConfirmationDialogButton
// } from '../../../../core/enums/confirmationDialogButton.enum';

@Component({
  selector: 'isc-input-control',
    imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule
  ],
  templateUrl: './input-control.html',
  styleUrl: './input-control.scss',
})
export class InputControl {

  // ======================================================
  // VIEW CHILD
  // ======================================================

  @ViewChild('customAutoComplete')
  autoCompleteControl?:
    MatAutocomplete;

  // ======================================================
  // INJECT
  // ======================================================

  private readonly sharedService =
    inject(SharedService);

  private readonly matDialog =
    inject(MatDialog);

  // ======================================================
  // INPUTS
  // ======================================================

  @Input() id = '';

  @Input() label = '';

  @Input({ required: true })
  ispFormControl!:
    FormControl;

  @Input() placeholder = '';

  @Input() icon = '';

  @Input() hint = '';

  @Input() minlength = 0;

  @Input() maxlength = 100;

  @Input() countChars = false;

  @Input() error = false;

  @Input()
  errorMessage =
    'Field is invalid';

  @Input() bold = false;

  @Input() suffixIcon = '';

  @Input()
  suffixIconTooltip = '';

  @Input()
  isDatePicker = false;

  @Input()
  numberMin = 1;

  @Input()
  numberMax = 1000;

  @Input()
  type = 'text';

  @Input()
  readonly = false;

  @Input()
  class = 'text-left';

  @Input()
  isAutocomplete = false;

  @Input()
  autocompleteOptions: any[] = [];

  @Input()
  autocompleteOptionViewKey =
    '';

  @Input()
  specialCharacterValidation =
    false;

  @Input()
  specialCharacterValidationErrorClass =
    '-mt-5';

  @Input()
  minDate: Date | null = null;

  @Input()
  maxDate: Date | null = null;

  @Input()
  digitValidation = false;

  // ======================================================
  // BRAND LOGO
  // ======================================================

  @Input()
  set brandLogo(
    value: string
  ) {

    if (!value) {
      return;
    }

    setTimeout(async () => {

      if (
        value.startsWith(
          'blob:'
        )
      ) {

        this.logo.set(value);

        return;
      }

      const fragments =
        value.split('.');

      const fileType =
        fragments[
          fragments.length - 1
        ];

      try {

        const mediaToken =
          await this
            .sharedService
            .apiService
            .getMediaToken(
              fileType
            );

        const payload = {

          authToken:
            mediaToken,

          pictureUrl:
            value
        };

        this.sharedService
          .profileService
          .getProfilePicture(
            payload
          )
          .subscribe({

            next: response => {

              this.logo.set(
                URL.createObjectURL(
                  response
                )
              );
            },

            error: () => {

              this.sharedService
                .alertService
                .error(
                  'Unable to fetch brand logo'
                );
            }
          });

      } catch (error) {

        console.log(error);
      }

    }, 0);
  }

  // ======================================================
  // OUTPUTS
  // ======================================================

  @Output()
  onBlur =
    new EventEmitter<void>();

  @Output()
  valueChange =
    new EventEmitter<any>();

  @Output()
  suffixIconClick =
    new EventEmitter<any>();

  @Output()
  isError =
    new EventEmitter<boolean>();

  @Output()
  onRemoveLogo =
    new EventEmitter<boolean>();

  // ======================================================
  // SIGNALS
  // ======================================================

  readonly showTooltip =
    signal(false);

  readonly logo =
    signal('');

  readonly filteredAutocompleteOptions =
    signal<any[]>([]);

  // ======================================================
  // VARIABLES
  // ======================================================

  readonly allowedCharacters =
    RegexLib
      .OnlyAllowedSpecialCharacters;

  // ======================================================
  // INIT
  // ======================================================

  ngOnInit(): void {

    if (
      this.isAutocomplete
    ) {

      this.filteredAutocompleteOptions.set(
        this.autocompleteOptions
      );
    }

    if (
      this.digitValidation
    ) {

      this.numberMin =
        this.getMinValue(
          this.numberMin
        );

      this.numberMax =
        this.getMaxValue(
          this.numberMax
        );
    }
  }

  // ======================================================
  // CHANGES
  // ======================================================

  ngOnChanges(): void {

    if (
      this.isAutocomplete
    ) {

      this.filteredAutocompleteOptions.set(
        this.autocompleteOptions
      );

      setTimeout(() => {

        const value =
          this
            .ispFormControl
            ?.value;

        if (
          !value ||
          !this.autoCompleteControl
        ) {
          return;
        }

        const option =
          this
            .autoCompleteControl
            .options
            .toArray()
            .find(
              (item: any) =>
                item.value ===
                value
            );

        if (option) {

          option.select();

          option.setActiveStyles();
        }

      });
    }

    if (
      this.minDate &&
      !isNaN(
        Number(
          this.minDate
        )
      )
    ) {

      this.minDate =
        new Date(
          this.minDate
        );
    }

    if (
      this.maxDate &&
      !isNaN(
        Number(
          this.maxDate
        )
      )
    ) {

      this.maxDate =
        new Date(
          this.maxDate
        );
    }
  }

  // ======================================================
  // SUFFIX CLICK
  // ======================================================

  suffixClick(
    event: Event
  ): void {

    this.suffixIconClick.emit(
      event
    );
  }

  // ======================================================
  // BLUR
  // ======================================================

  handleBlur(): void {

    if (
      this.type === 'number'
    ) {

      const value =
        Number(
          this
            .ispFormControl
            .value
        );

      if (
        this.numberMax &&
        value >=
        this.numberMax
      ) {

        this
          .ispFormControl
          .setValue(
            this.numberMax
          );

      } else if (
        (
          this.numberMin ||
          this.numberMin === 0
        ) &&
        value <=
        this.numberMin
      ) {

        this
          .ispFormControl
          .setValue(
            this.numberMin
          );

      } else {

        this
          .ispFormControl
          .setValue(
            Math.round(
              value
            )
          );
      }
    }

    this.onBlur.emit();
  }

  // ======================================================
  // MODEL CHANGE
  // ======================================================

  handleModelChange(
    event: any
  ): void {

    // ============================================
    // SPECIAL CHARACTER VALIDATION
    // ============================================

    if (
      !this.isDatePicker &&
      this.type !== 'number' &&
      this.type !== 'email' &&
      this.specialCharacterValidation
    ) {

      const isValid =
        this.sharedService
          .OnlyAllowedCharactersPresent(
            event
          );

      if (!isValid) {

        this
          .ispFormControl
          .setErrors({
            invalidInput: true
          });
      }
    }

    // ============================================
    // AUTOCOMPLETE
    // ============================================

    if (
      this.isAutocomplete
    ) {

      const filtered =
        this.autocompleteOptions
          .filter(
            (item: any) => {

              if (!event) {
                return true;
              }

              return item[
                this
                  .autocompleteOptionViewKey
              ]
                ?.toLowerCase()
                ?.includes(
                  event.toLowerCase()
                );
            }
          );

      this
        .filteredAutocompleteOptions
        .set(filtered);

      return;
    }

    // ============================================
    // EMAIL
    // ============================================

    if (
      this.type === 'email' &&
      !this.error
    ) {

      this.error =
        !this
          .sharedService
          .isValidEmail(
            this
              .ispFormControl
              .value
          );
    }

    this.valueChange.emit(
      this
        .ispFormControl
        .value
    );

    this.isError.emit(
      this.error
    );
  }

  // ======================================================
  // DIGIT MIN
  // ======================================================

  getMinValue(
    numDigits: number
  ): number {

    if (
      numDigits <= 0
    ) {
      return 0;
    }

    return Math.pow(
      10,
      numDigits - 1
    );
  }

  // ======================================================
  // DIGIT MAX
  // ======================================================

  getMaxValue(
    numDigits: number
  ): number {

    if (
      numDigits <= 0
    ) {
      return 0;
    }

    return (
      Math.pow(
        10,
        numDigits
      ) - 1
    );
  }

  // ======================================================
  // REMOVE LOGO
  // ======================================================

  onRemoveLogoClick():
    void {

    const dialogRef =
      this.matDialog.open(
        ConfirmationDialog,
        {

          disableClose: true,

          data: {

            title:
              'Remove Logo',

            message:
              'Are you certain you wish to remove the logo? If yes, please ensure to save the changes before leaving the page.',

            primaryButton:
              'Yes, remove',

            secondaryButton:
              'No, cancel'
          }
        }
      );

    dialogRef
      .afterClosed()
      .subscribe(result => {

        if (
          result ===
          ConfirmationDialogButton
            .PRIMARY
        ) {

          this.onRemoveLogo.emit(
            true
          );

          this.logo.set('');
        }
      });
  }
}